import './coapplicantEmploymentTypePage.scss';

import KeyboardBackspaceRoundedIcon from '@mui/icons-material/KeyboardBackspaceRounded';
import MenuRoundedIcon from '@mui/icons-material/MenuRounded';
import LoadingButton from '@mui/lab/LoadingButton';
import Alert from '@mui/material/Alert';
import Box from '@mui/material/Box';
import LinearProgress from '@mui/material/LinearProgress';
import ThemeProvider from '@mui/material/styles/ThemeProvider';
import { EmploymentType } from '@thesavingsgroup/enums/EmploymentType';
import { isEmpty } from 'lodash';
import { useEffect, useState } from 'react';
import { FieldValues, useForm } from 'react-hook-form';
import { useTranslation } from 'react-i18next';
import { useNavigate } from 'react-router-dom';

import { NavigationPane } from '../../../components/NavigationPane/NavigationPane';
import { RadioGroup } from '../../../components/RadioGroup/RadioGroup';
import { RadioOptionClickEvent } from '../../../components/RadioGroup/types/RadioOptionClickEvent';
import { isValidClickEvent } from '../../../components/utils/isValidClickEvent';
import useCoapplicantStore from '../../../store/coapplicantStore/useCoapplicantStore';
import useNavigationPaneStore from '../../../store/navigationPaneStore/useNavigationPaneStore';
import useThemeStore from '../../../store/themeStore/useThemeStore';
import useWorkflowStore from '../../../store/workflowStore/useWorkflowStore';
import { locator } from '../../../testing/e2e-locator/locator';

export function Component(): JSX.Element {
  const { createTheme } = useThemeStore();
  const theme = createTheme();
  const navigate = useNavigate();

  const { t } = useTranslation();
  const {
    computed: { progress },
    next,
    previous,
  } = useWorkflowStore();
  const { setDrawerOpen } = useNavigationPaneStore();

  const [isInitialSelection, setIsInitialSelection] = useState(false);

  const {
    computed: { isCoapplicantPersisted },
    coapplicantCurrentIncomeEmploymentType,
    isSyncingCoapplicant,
    syncingCoapplicantFailedMessage,
    setCoapplicantCurrentIncomeEmploymentType,
    syncCoapplicant,
  } = useCoapplicantStore();

  const {
    formState: { isDirty },
    handleSubmit,
    register,
    watch,
  } = useForm({
    defaultValues: {
      employmentTypeValue: coapplicantCurrentIncomeEmploymentType,
    },
  });

  const previousStep = (): void => {
    navigate(previous());
  };

  const radioOptionClicked = (
    event: RadioOptionClickEvent<EmploymentType>,
  ): void => {
    if (!('target' in event && 'value' in event.target)) {
      return;
    }

    const employmentTypeValue = event.target.value;

    setIsInitialSelection(
      isEmpty(coapplicantCurrentIncomeEmploymentType) &&
        isValidClickEvent(event),
    );

    setCoapplicantCurrentIncomeEmploymentType(employmentTypeValue);
  };

  const formSubmit = async (values: FieldValues): Promise<void> => {
    const { employmentTypeValue } = values;

    if (!employmentTypeValue) {
      return;
    }

    if (isCoapplicantPersisted && isDirty) {
      const synced = await syncCoapplicant();
      if (synced) {
        navigate(next());
      }

      return;
    }

    navigate(next());
  };

  useEffect(() => {
    if (isInitialSelection) {
      handleSubmit(formSubmit)();
    }
  }, [isInitialSelection]);

  return (
    <ThemeProvider theme={theme}>
      <Box className="coapplicant-employment-type-page" sx={theme.pageLayout}>
        <header
          data-testid={locator.pages.coapplicantEmploymentType.header.root}
        >
          <button
            aria-label="Previous step"
            data-testid={
              locator.pages.coapplicantEmploymentType.header.previousStepButton
            }
            type="button"
            onClick={previousStep}
          >
            <KeyboardBackspaceRoundedIcon />
          </button>
          <button
            aria-label="Toggle drawer"
            data-testid={
              locator.pages.coapplicantEmploymentType.header.drawerButton
            }
            type="button"
            onClick={() => {
              setDrawerOpen(true);
            }}
          >
            <MenuRoundedIcon />
          </button>
        </header>

        <div className="progress-container" role="complementary">
          <LinearProgress
            aria-label="Application completed percentage"
            data-testid={locator.pages.coapplicantEmploymentType.progress.root}
            value={progress}
            variant="determinate"
          />
        </div>

        <main>
          <form
            data-testid={locator.pages.coapplicantEmploymentType.form.root}
            noValidate
            onSubmit={handleSubmit(formSubmit)}
          >
            <div className="form-content">
              <h1 data-testid={locator.pages.coapplicantEmploymentType.form.h1}>
                {t('CoapplicantEmploymentTypePage.title')}
              </h1>
              <h2 data-testid={locator.pages.coapplicantEmploymentType.form.h2}>
                {t('CoapplicantEmploymentTypePage.subtitle')}
              </h2>

              {!isEmpty(syncingCoapplicantFailedMessage) && (
                <div style={{ marginBottom: '2rem' }}>
                  <Alert
                    data-testid={
                      locator.pages.coapplicantEmploymentType.alert.error
                    }
                    icon={false}
                    severity="error"
                  >
                    {syncingCoapplicantFailedMessage}
                  </Alert>
                </div>
              )}

              <div style={{ marginBottom: '3rem' }}>
                <RadioGroup
                  defaultValue={coapplicantCurrentIncomeEmploymentType}
                  name="employmentTypeValue"
                  options={[
                    {
                      label: t('CoapplicantEmploymentTypePage.fullTime'),
                      value: EmploymentType.FULL_TIME,
                      testId:
                        locator.pages.coapplicantEmploymentType.form
                          .radioOption1,
                    },
                    {
                      label: t('CoapplicantEmploymentTypePage.partTime'),
                      value: EmploymentType.PART_TIME,
                      testId:
                        locator.pages.coapplicantEmploymentType.form
                          .radioOption2,
                    },
                    {
                      label: t('CoapplicantEmploymentTypePage.selfEmployed'),
                      value: EmploymentType.SELF_EMPLOYED,
                      testId:
                        locator.pages.coapplicantEmploymentType.form
                          .radioOption3,
                    },
                    {
                      label: t('CoapplicantEmploymentTypePage.military'),
                      value: EmploymentType.ACTIVE_MILITARY,
                      testId:
                        locator.pages.coapplicantEmploymentType.form
                          .radioOption4,
                    },
                  ]}
                  register={register}
                  testId={
                    locator.pages.coapplicantEmploymentType.form.radioGroupRoot
                  }
                  watch={watch}
                  onClick={radioOptionClicked}
                />
              </div>

              <div style={{ marginBottom: '3rem' }}>
                <LoadingButton
                  color="primary"
                  data-testid={
                    locator.pages.coapplicantEmploymentType.form.submitButton
                  }
                  disabled={isEmpty(coapplicantCurrentIncomeEmploymentType)}
                  loading={isSyncingCoapplicant}
                  startIcon={<></>}
                  type="submit"
                  variant="contained"
                  fullWidth
                >
                  {t('CoapplicantEmploymentTypePage.continue')}
                </LoadingButton>
              </div>
            </div>
            <footer
              data-testid={locator.pages.coapplicantEmploymentType.footer.root}
            >
              {t('copyright', { year: new Date().getFullYear() })}
            </footer>
          </form>
        </main>
      </Box>

      <NavigationPane withDrawer withUserNameHeader />
    </ThemeProvider>
  );
}

Component.displayName = 'CoapplicantEmploymentTypePage';
